import { useMemo, useState } from 'react'
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core'

/**
 * ConfigurableDataTable (Material-UI v4)
 * - Columns and rows are fully configurable via props.
 * - Supports text search on selected columns.
 * - Optional sorting by clicking on sortable headers.
 */
export default function ConfigurableDataTable({
    rows = [],
    columns = [],
    searchable = true,
    initialQuery = '',
    emptyText = 'No data available.',
    striped = true,
    rowClassName, // (row, index) => string
}) {
    const [q, setQ] = useState(initialQuery);
    const [sortState, setSortState] = useState(null); // { key, dir: 'asc'|'desc' }

    const searchableColumns = useMemo(
        () => columns.filter(c => c.searchable !== false),
        [columns]
    );

    const toggleSort = (col) => {
        if (!col.sortable) return;
        setSortState(prev => {
            if (!prev || prev.key !== col.key) return { key: col.key, dir: 'asc' };
            if (prev.dir === 'asc') return { key: col.key, dir: 'desc' };
            return null; // third click clears sorting
        });
    };

    const getCellRawValue = (row, col) => {
        try {
            if (typeof col.accessor === 'function') return col.accessor(row);
            if (col.key in row) return row[col.key];
            return undefined;
        } catch {
            return undefined;
        }
    };

    const renderCellValue = (row, col) => {
        const raw = getCellRawValue(row, col);
        if (col.format) return col.format(raw, row);
        return raw ?? '-';
    };

    const filtered = useMemo(() => {
        let result = Array.isArray(rows) ? [...rows] : [];
        if (q.trim() && searchable) {
            const needle = q.trim().toLowerCase();
            result = result.filter(row => {
                return searchableColumns.some(col => {
                    const v = getCellRawValue(row, col);
                    return String(v ?? '').toLowerCase().includes(needle);
                });
            });
        }
        return result;
    }, [rows, q, searchable, searchableColumns]);

    const sorted = useMemo(() => {
        if (!sortState) return filtered;
        const col = columns.find(c => c.key === sortState.key);
        if (!col) return filtered;
        const dir = sortState.dir === 'desc' ? -1 : 1;
        const copy = [...filtered];
        copy.sort((a, b) => {
            const va = getCellRawValue(a, col);
            const vb = getCellRawValue(b, col);
            if (va == null && vb == null) return 0;
            if (va == null) return -1 * dir;
            if (vb == null) return 1 * dir;
            const na = Number(va);
            const nb = Number(vb);
            if (!Number.isNaN(na) && !Number.isNaN(nb)) return (na - nb) * dir;
            return String(va).localeCompare(String(vb)) * dir;
        });
        return copy;
    }, [filtered, sortState, columns]);

    const headerCellClass = 'text-xs font-semibold whitespace-nowrap select-none cursor-pointer';
    const bodyCellBase = 'whitespace-nowrap text-xs tabular-nums';

    return (
        <div className="w-full h-full flex flex-col gap-2">
            {searchable && (
                <div className="w-full flex items-center gap-2">
                    <input
                        type="text"
                        value={q}
                        onChange={e => setQ(e.target.value)}
                        placeholder="Search…"
                        className="border border-black/10 rounded px-2 py-1 text-sm focus:outline-none focus:ring focus:ring-black/10"
                    />
                </div>
            )}

            <TableContainer component={Paper} style={{ height: '100%' }}>
                <Table className="w-full shadow-2xl">
                    <TableHead>
                        <TableRow>
                            {columns.map(col => {
                                const isSorted = sortState?.key === col.key;
                                const dir = isSorted ? sortState?.dir : null;
                                return (
                                    <TableCell
                                        key={col.key}
                                        align={col.align || 'left'}
                                        className={`${headerCellClass} ${col.headerClassName || ''}`}
                                        onClick={() => toggleSort(col)}
                                        style={col.width ? { width: col.width } : undefined}
                                    >
                                        <span className="inline-flex items-center gap-8">
                                            {col.header ?? col.key}
                                            {col.sortable && (
                                                <span className="text-10 opacity-60">
                                                    {dir === 'asc' ? '▲' : dir === 'desc' ? '▼' : '↕'}
                                                </span>
                                            )}
                                        </span>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {sorted.length > 0 ? (
                            sorted.map((row, idx) => {
                                const stripedClass = striped && (idx % 2 === 1) ? 'bg-gray-100' : '';
                                const extra = typeof rowClassName === 'function' ? (rowClassName(row, idx) || '') : '';
                                return (
                                    <TableRow key={idx} className={`${stripedClass} ${extra}`}>
                                        {columns.map(col => (
                                            <TableCell
                                                key={col.key}
                                                align={col.align || 'left'}
                                                className={`${bodyCellBase} ${col.className || ''}`}
                                            >
                                                {renderCellValue(row, col)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center" className="py-16 opacity-60 text-xs">
                                    {emptyText}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}
